import { useEffect } from 'react';

interface PageSeoOptions {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  jsonLd?: object[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function usePageSeo(options: PageSeoOptions) {
  const { title, description, keywords, canonical, jsonLd } = options;

  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }
    if (keywords) upsertMeta('name', 'keywords', keywords);
    if (title) {
      upsertMeta('property', 'og:title', title);
      upsertMeta('name', 'twitter:title', title);
    }
    if (canonical) {
      let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }
  }, [title, description, keywords, canonical]);

  useEffect(() => {
    if (!jsonLd || jsonLd.length === 0) return;
    const existing = document.head.querySelectorAll('script[data-seo-jsonld]');
    existing.forEach((el) => el.remove());
    jsonLd.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }, [jsonLd]);
}